import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  useRenderCount,
  useMountTiming,
  perfMark,
  perfMeasure,
} from "../utils/perfLogger";
import { type PreOrder, PreOrderStatus } from "@/app/api/types/preOrder";
import type { ProductDefaults } from "../utils/resolveProductDefaults";
import { useFlowHandlers } from "../hooks/useFlowHandlers";
import { useProposalFlowHandlers } from "../hooks/useProposalFlowHandlers";
import { useLiveFlowsForPreOrder } from "../hooks/useLiveFlowsForPreOrder";
import { useRecapAiSuggestions } from "../hooks/useRecapAiSuggestions";
import { projectFlows } from "../services/aiSuggestionProjection";
import { useModifyBuffer } from "../hooks/useModifyBuffer";
import { usePreOrderLock } from "../hooks/usePreOrderLock";
import { ModifyBufferProvider } from "../contexts/ModifyBufferContext";
import { PreOrderLockProvider } from "../contexts/PreOrderLockContext";
import { useRecapHeaderComponent } from "../contexts/RecapPrimitivesContext";
import { FlowsTable } from "./FlowsTable";
import { RecapModifyCancelSlot } from "../actions/RecapActionBar";
import { PreOrderCommentBlock } from "./PreOrderCommentBlock";
import {
  useIsSelectedPreOrder,
  useIsHighlightedPreOrder,
  useIsRecentlyCreatedPreOrder,
  useChatPanelSelectionStore,
} from "../stores/chatPanelSelectionStore";
import {
  useChatPanelActionsOptional,
  useConflictHighlightContextOptional,
  useRecapDataContextOptional,
} from "../contexts/RecapContext";
import { cn } from "@/lib/utils";
import type { ColumnResizeProps } from "./FlowsTable";
import { isProposalPreOrder } from "../types/proposal.types";
import { derivePreOrderSource } from "../utils/sourceUtils";
import type { ColumnDef } from "../config/types";
import { FlowBatchIsland } from "./FlowBatchIsland";

interface PreOrderSectionProps {
  preOrder: PreOrder;
  onPartnerChange?: (preOrderId: string) => void;
  columnResize?: ColumnResizeProps;
  /**
   * Focus layout only — publishes this section's modify buffer up to the
   * focus page. The focus hero action bar renders `ModifyControls` from
   * this buffer for AI proposals (so accept/decline lives there instead
   * of duplicated big buttons). Fires whenever the buffer object changes
   * (modify state, pending edits, flush state).
   */
  onModifyStateChange?: (buffer: ReturnType<typeof useModifyBuffer>) => void;
  /** Override columns when used outside RecapProvider (e.g. in chat). */
  columns?: ColumnDef[];
  /**
   * When true and the pre-order is a pending proposal, the table stays
   * interactive so the user can edit the proposal payload before approving.
   * When false (default), proposal pre-orders render read-only.
   */
  editableProposal?: boolean;
  /**
   * Forwarded to <FlowsTable>. `spacious` is used by the focused single-PO
   * page where the table owns the page width; `compact` (default) keeps the
   * legacy tight layout used inside partner cards / chat-panel previews.
   */
  density?: "compact" | "spacious";
  /**
   * Focus-page layout. When true:
   *  - The inner `<PreOrderHeader>` strip is skipped — the focus page
   *    renders its own order-identity header above this card.
   *  - The flows table is wrapped in its own card with a top bar
   *    ("Lignes de commande · N" + ModifyControls) and a totals footer.
   *  - The card owns its visual chrome; callers shouldn't wrap it again.
   */
  focusLayout?: boolean;
  batchIslandRoot?: HTMLDivElement | null;
}

export const PreOrderSection = memo(function PreOrderSection({
  preOrder,
  onPartnerChange,
  columnResize,
  onModifyStateChange,
  columns,
  editableProposal = false,
  density = "compact",
  focusLayout = false,
  batchIslandRoot = null,
}: PreOrderSectionProps) {
  const { t } = useTranslation();
  useRenderCount(`PreOrderSection[${preOrder.id.slice(0, 8)}]`);
  useMountTiming(`PreOrderSection[${preOrder.id.slice(0, 8)}]`);
  const selectionStore = useChatPanelSelectionStore();
  // Stable actions only — PreOrderSection uses imperative panel access in
  // its row click handler. Avoids re-renders for every section when the
  // chat panel opens/closes (a partner card with 5 POs would render 5
  // sections; per-product view fans out further).
  const chatPanelActions = useChatPanelActionsOptional();
  // Optional — null when this section is rendered outside the recap provider.
  // Drives the viewer-seat rewrite passed to `derivePreOrderSource` so the
  // chat panel opens on the viewer's own seat (not the PO's trigger user)
  // for shared-group conversations.
  const recapData = useRecapDataContextOptional();
  const sectionRef = useRef<HTMLDivElement>(null);
  const isSelected = useIsSelectedPreOrder(preOrder.id);
  const isHighlighted = useIsHighlightedPreOrder(preOrder.id);
  const isRecentlyCreated = useIsRecentlyCreatedPreOrder(preOrder.id);
  const conflictHighlight = useConflictHighlightContextOptional();
  const isConflicted =
    conflictHighlight?.conflictedIds.has(preOrder.id) ?? false;

  // Scroll into view when navigated to (e.g. from LinkedPreOrdersBar badge click)
  useEffect(() => {
    if (isHighlighted && sectionRef.current) {
      // Find the nearest scrollable ancestor
      const scrollContainer = sectionRef.current.closest(
        ".overflow-y-auto",
      ) as HTMLElement | null;

      if (!scrollContainer) {
        // Fallback: just scroll after a short delay
        const timer = setTimeout(() => {
          sectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 200);
        return () => clearTimeout(timer);
      }

      // Wait for the first scroll (group-level) to finish, then scroll to this section
      const onScrollEnd = () => {
        scrollContainer.removeEventListener("scrollend", onScrollEnd);
        clearTimeout(fallback);
        // Small delay for the group to finish expanding
        sectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      };

      scrollContainer.addEventListener("scrollend", onScrollEnd, {
        once: true,
      });

      // Fallback if scrollend never fires (e.g. already at position, no scroll needed)
      const fallback = setTimeout(() => {
        console.log("fallback");
        scrollContainer.removeEventListener("scrollend", onScrollEnd);
        sectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 600);

      return () => {
        scrollContainer.removeEventListener("scrollend", onScrollEnd);
        clearTimeout(fallback);
      };
    }
  }, [isHighlighted]);

  const isProposal = isProposalPreOrder(preOrder);
  // Proposal pre-orders default to read-only; the user opts into editing via
  // the "Edit" button on the proposal action bar. `editableProposal` gates
  // whether this surface allows the toggle at all (always true in recap).
  const isPendingProposal =
    isProposal && preOrder.__proposal.batch.status === "pending";
  const canToggleEdit = isPendingProposal && editableProposal;
  const [isEditingProposal, setIsEditingProposal] = useState(false);
  const effectivelyEditable = canToggleEdit && isEditingProposal;
  const useProposalRouting = isProposal && effectivelyEditable;

  const handleToggleEditProposal = useCallback(() => {
    setIsEditingProposal((v) => !v);
  }, []);

  // Direct mount: render FlowsTable in the same commit as the partner
  // section. The previous skeleton + `useTransition` path was added when
  // FlowsTable mounts were expensive (~150 ms each), to give the user
  // immediate visual feedback while the real table reconciled in a low-
  // priority lane. After the Radix de-lazification work (commits
  // 3ec8a497, 0beb4937, 4ff0b365), FlowsTable mounts in ~12–40 ms.
  //
  // The skeleton was now costing more than it saved:
  //   - Skeleton commit (~46 ms)
  //   - Strict-mode double mount of PreOrderSection in dev (~60 ms)
  //   - Transition queue wait (~30 ms)
  //   - Final atomic swap to FlowsTable (~12 ms)
  // = ~150 ms of skeleton time vs. ~50 ms direct mount.
  //
  // Also fixes the user-perceived "header appears before rows" stagger:
  // the swap from gray skeleton to populated cells reads as a two-phase
  // reveal even though both phases commit atomically. Direct mount has
  // a single commit, so the card grows to its final size in one frame.
  const skeletonStartRef = useRef<number>(perfMark());
  const tableReadyLoggedRef = useRef(false);
  useEffect(() => {
    if (!tableReadyLoggedRef.current) {
      tableReadyLoggedRef.current = true;
      perfMeasure(
        `PreOrderSection[${preOrder.id.slice(0, 8)}] direct mount`,
        skeletonStartRef.current,
      );
    }
  }, [preOrder.id]);

  const liveFallback = useFlowHandlers({ preOrderId: preOrder.id });
  const proposalFallback = useProposalFlowHandlers({ preOrder });
  const fallback = useProposalRouting ? proposalFallback : liveFallback;
  // Repack into a stable object so `useModifyBuffer`'s callback deps don't
  // see a fresh literal each render — without this, every render bumps
  // the buffer's `handleFlowUpdate` / `handleFlowDelete` identity, which
  // bubbles down to every `<FlowRow>` and busts its `React.memo`.
  const stableFallback = useMemo(
    () => ({
      handleFlowUpdate: fallback.handleFlowUpdate,
      handleFlowCreate: fallback.handleFlowCreate,
      handleFlowDelete: fallback.handleFlowDelete,
    }),
    [
      fallback.handleFlowUpdate,
      fallback.handleFlowCreate,
      fallback.handleFlowDelete,
    ],
  );

  // AI_PROPOSAL (V3 whole-pre-order AI proposal) shares the editing rules
  // of PENDING: the row is open for accept/decline + direct edits without
  // an explicit "Edit" toggle.
  const isPendingLike =
    preOrder.status === PreOrderStatus.PENDING ||
    preOrder.status === PreOrderStatus.AI_PROPOSAL;

  // PENDING (or AI_PROPOSAL) non-legacy-proposal cells stay editable at all
  // times: the first edit optimistically enters modify mode and writes to
  // the buffer.
  const autoEnterOnEdit = isPendingLike && !isProposal;

  // Statuses other than PENDING / AI_PROPOSAL / CONFIRMED are terminal /
  // locked (CANCELLED, EXPORTED, RESERVED, SENT, PAID, EXPORT_FAILED) — the
  // legacy per-flow PUT path does NOT gate these on the backend, so cells
  // must be locked here to prevent persisted edits.
  const isLockedStatus =
    !isPendingLike && preOrder.status !== PreOrderStatus.CONFIRMED;

  // Per-PO Dexie subscription — replaces `preOrder.flows` from the central
  // recap pipeline as this section's data source. After save, modify-mode
  // exit no longer waits on the ~1 s central re-fetch; this fires within
  // ~10 ms of the reconcile commit.
  //
  // Proposal pre-orders are synthetic (`buildVirtualPreOrder` /
  // `buildShellFromFlows` mint an id like `proposal-<batchId>` that doesn't
  // exist in `preOrderFlows`). Their flows live on the prop, baked from
  // `batch.proposals`, and the Dexie query would always return [] for that
  // synthetic id. Read flows from the prop in that case.
  const rawDexieFlows = useLiveFlowsForPreOrder(preOrder.id);
  // Project typed AI flow-suggestion rows onto the Dexie flows so the flow
  // cells / badges paint the AI diff (the live query carries no embedded
  // `ai_suggestions`; the typed rows are the source of truth). Identity-stable
  // so `React.memo` flow rows don't churn when no suggestions apply.
  const { maps: aiSuggestionMaps } = useRecapAiSuggestions();
  const dexieFlows = useMemo(
    () => projectFlows(rawDexieFlows, aiSuggestionMaps),
    [rawDexieFlows, aiSuggestionMaps],
  );
  // Dexie's live subscription returns [] for one render before resolving,
  // even though `preOrder.flows` from the central recap pipeline is
  // already populated. That [] → flows transition was the real cause of
  // "header appears before rows" — FlowsTable rendered with 0 rows on
  // first mount, then re-rendered with N rows ~150 ms later when Dexie
  // came back.
  //
  // Fix: use `preOrder.flows` until Dexie produces a non-empty response
  // at least once, then latch to Dexie for the rest of the session.
  // After the latch, deletions back to 0 stay on Dexie (correct empty
  // state). For POs that genuinely have 0 flows, both sources are [] and
  // the user sees the empty table immediately.
  const [dexieHasData, setDexieHasData] = useState(false);
  useEffect(() => {
    if (!dexieHasData && dexieFlows.length > 0) setDexieHasData(true);
  }, [dexieHasData, dexieFlows.length]);
  const liveFlows = isProposal
    ? (preOrder.flows ?? [])
    : dexieHasData
      ? dexieFlows
      : (preOrder.flows ?? dexieFlows);

  const buffer = useModifyBuffer({
    preOrder,
    liveFlows,
    fallback: stableFallback,
    autoEnterOnEdit,
  });

  // Per-PO hard lock: covers local in-flight saves + AI approvals AND the
  // server-side export lock (M3 push in progress / failed). When locked,
  // every editable surface on this section reads as read-only — see
  // `usePreOrderLock` for the source matrix and race-safety rationale.
  // ModifyControls + cells consume this via `PreOrderLockContext`.
  //
  // AI in-flight is read inside `usePreOrderLock` from the module-level
  // `aiInFlightStore` rather than from a local `useAiPreOrderActions`
  // call — sibling instances of that hook don't share state, so wiring
  // it here would never see ModifyControls' approve / decline. See
  // `aiInFlightStore.ts` for the cubic PR #1467 review thread.
  const lock = usePreOrderLock({
    preOrder,
    isFlushing: buffer.isFlushing,
  });

  // `useModifyBuffer` returns a fresh object literal every render, so
  // depending on `buffer` directly would re-publish — and re-render the
  // focus page — on every PreOrderSection render (i.e. every keystroke).
  // Dep instead on the fields the published buffer's consumers actually
  // react to: the hero `ModifyControls` (modify state, pending edits,
  // flush / error) and the focus history "+" handler
  // (`createFlowFromHistory`). The effect body still publishes the whole
  // `buffer` so callers get the latest closures.
  useEffect(() => {
    onModifyStateChange?.(buffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    buffer.isModifying,
    buffer.isFlushing,
    buffer.lastError,
    buffer.pendingInfo,
    buffer.createFlowFromHistory,
    onModifyStateChange,
  ]);

  const flows = buffer.isModifying ? buffer.bufferedFlows : liveFlows;
  // PENDING non-proposal cells are always editable (auto-enters modify mode
  // on first keystroke). CONFIRMED requires explicit modify-mode entry
  // (read-only otherwise — gates the ERP re-export path). Proposals stay
  // read-only until the user toggles edit via the proposal action bar.
  // While a flush/confirm RPC is in flight, lock the table immediately so
  // the user can't keep typing or adding flows into a buffer that's about
  // to be wiped by clearBuffer once the status flip lands.
  const tableDisabled =
    isLockedStatus ||
    // Hard lock from any in-flight or server-side reason (push-batch
    // flush, AI approve/decline, export in progress, export failed).
    // `buffer.isFlushing` is already included inside `lock.isLocked`
    // via the hook's input, so this single term subsumes the legacy
    // local `buffer.isFlushing` check (greptile PR #1467 review).
    lock.isLocked ||
    (!buffer.isModifying &&
      (preOrder.status === PreOrderStatus.CONFIRMED ||
        (isProposal && !effectivelyEditable)));

  const isDeleted = preOrder.deleted_at != null;
  const isExported = preOrder.status === PreOrderStatus.EXPORTED;
  // V3 whole-PO AI proposal (status=8). Mirrors the modify-mode pattern:
  // section gets a subtle violet wash, the bold ring lives on the partner
  // card border (see PartnerGroup `anyAiProposal`). Takes precedence over
  // modify-mode styling since cells auto-enter modify on an AI_PROPOSAL
  // parent — the violet signal should win until the user accepts / declines.
  const isAiProposalPO = preOrder.status === PreOrderStatus.AI_PROPOSAL;

  // Focus layout owns its own chrome (white card with border + shadow on
  // FocusedTableCard). The list-style left-rail accent + bg tints are
  // noise there — there's only one PO and no list to scan, so highlighting
  // it doesn't aid recognition.
  let sectionStyle = focusLayout ? "" : "border-l-2 border-transparent";
  if (focusLayout) {
    // No status-driven tints in focus layout — keep the card neutral.
  } else if (isProposal) {
    sectionStyle =
      "border-l-2 border-l-violet-400 border border-dashed border-violet-200 bg-violet-50/50 rounded-md";
  } else if (isDeleted) {
    sectionStyle =
      "bg-red-50/40 border-l-2 border-l-red-400 text-red-400 opacity-60";
  } else if (buffer.isModifying && buffer.lastError) {
    sectionStyle = "bg-red-50/40 border-l-2 border-l-red-500";
  } else if (isAiProposalPO) {
    // AI-proposal signal lives on the partner card border. Section keeps a
    // subtle violet tint matching the modify-mode pattern.
    sectionStyle = "bg-violet-50/40 border-l-2 border-transparent";
  } else if (buffer.isModifying) {
    // Modify-mode signal lives on the partner card border. Section keeps a
    // very subtle orange tint without the heavy left rail.
    sectionStyle = "bg-orange-50/20 border-l-2 border-transparent";
  } else if (isHighlighted || isRecentlyCreated) {
    sectionStyle = "bg-blue-100/60 border-l-2 border-l-blue-500";
  } else if (isSelected) {
    sectionStyle = "bg-blue-50/30 border-l-2 border-l-blue-500";
  } else if (isExported) {
    sectionStyle = "bg-green-50/40 border-l-2 border-l-green-500";
  }

  const batchIsland = focusLayout ? (
    <FlowBatchIsland
      preOrder={preOrder}
      flows={flows ?? []}
      onFlowDelete={buffer.handleFlowDelete}
      disabled={tableDisabled}
      isModifying={buffer.isModifying}
      pendingChangeCount={buffer.pendingInfo.total}
      isFlushing={buffer.isFlushing}
      onValidate={buffer.flushSave}
    />
  ) : null;

  return (
    <ModifyBufferProvider value={buffer}>
      <PreOrderLockProvider value={lock}>
        <div
          ref={sectionRef}
          data-testid="preorder-section"
          data-conflict={isConflicted ? "true" : undefined}
          data-preorder-id={preOrder.id}
          data-lock-reason={lock.reason ?? undefined}
          className={cn(
            "transition-colors duration-500 rounded-md",
            sectionStyle,
            // Two-shot blue ring on a freshly created/merged pre-order so it
            // pops out of the list. The recently-created flag auto-clears in
            // ~2.5s; the animation completes in ~1.4s.
            isRecentlyCreated && "animate-recap-recent-pulse",
            // 5s yellow ring after a batch-merge preflight rejects this row
            // (TTL managed by useConflictHighlight). Distinct hue from the
            // selection/highlight blues + the exported green so the user can
            // tell "this is the offender" at a glance even with the dialog
            // closed.
            isConflicted &&
              "ring-2 ring-yellow-400 ring-offset-1 transition-shadow",
          )}
          onClick={() => {
            if (isProposal) return;
            const isDifferentPO =
              selectionStore.getSnapshot().selectedPreOrderId !== preOrder.id;
            // When the panel is open (regardless of which view — message,
            // browser, or conversation), pivot it to this PO's primary source
            // so the user doesn't have to re-click LinkedSourcesPopover.
            // Same-PO clicks stay no-ops to preserve a deliberately-picked email.
            if (isDifferentPO && chatPanelActions?.getIsPanelOpen()) {
              chatPanelActions.openPanel(
                preOrder.id,
                derivePreOrderSource(preOrder, {
                  currentUserId: recapData?.user.id,
                  viewerWhatsappChats: recapData?.viewerWhatsappChats,
                  viewerSmsChats: recapData?.viewerSmsChats,
                }),
              );
              return;
            }
            if (isDifferentPO) {
              chatPanelActions?.setSelectedEmailId(null);
            }
            selectionStore.setSelectedPreOrderId(preOrder.id);
          }}
        >
          {focusLayout ? (
            <div className="relative">
              <FocusedTableCard
                preOrder={preOrder}
                flows={flows}
                tableDisabled={tableDisabled}
                buffer={buffer}
                columnResize={columnResize}
                columns={columns}
                density={density}
                t={t}
              />
              {batchIslandRoot ? (
                createPortal(batchIsland, batchIslandRoot)
              ) : (
                <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center">
                  {batchIsland}
                </div>
              )}
            </div>
          ) : (
            <>
              <HeaderSlot
                preOrder={preOrder}
                isEditingProposal={isEditingProposal}
                onToggleEditProposal={
                  canToggleEdit ? handleToggleEditProposal : undefined
                }
              />
              <FlowsTable
                preOrder={preOrder}
                flows={flows}
                disabled={tableDisabled}
                onFlowUpdate={buffer.handleFlowUpdate}
                onFlowCreate={buffer.handleFlowCreate}
                onFlowDelete={buffer.handleFlowDelete}
                partnerId={preOrder.partner_id}
                columnResize={columnResize}
                columns={columns}
                density={density}
              />
            </>
          )}
        </div>
      </PreOrderLockProvider>
    </ModifyBufferProvider>
  );
}, preOrderSectionAreEqual);

function HeaderSlot(
  props: Readonly<{
    preOrder: PreOrder;
    isEditingProposal?: boolean;
    onToggleEditProposal?: () => void;
  }>,
) {
  const Header = useRecapHeaderComponent();
  return <Header {...props} />;
}

function preOrderSectionAreEqual(
  prev: PreOrderSectionProps,
  next: PreOrderSectionProps,
) {
  return (
    prev.preOrder === next.preOrder &&
    prev.onPartnerChange === next.onPartnerChange &&
    prev.columnResize === next.columnResize &&
    prev.onModifyStateChange === next.onModifyStateChange &&
    prev.columns === next.columns &&
    prev.editableProposal === next.editableProposal &&
    prev.density === next.density &&
    prev.focusLayout === next.focusLayout &&
    prev.batchIslandRoot === next.batchIslandRoot
  );
}

interface FocusedTableCardProps {
  preOrder: PreOrder;
  flows: PreOrder["flows"];
  tableDisabled: boolean;
  buffer: ReturnType<typeof useModifyBuffer>;
  columnResize?: PreOrderSectionProps["columnResize"];
  columns?: PreOrderSectionProps["columns"];
  density: NonNullable<PreOrderSectionProps["density"]>;
  t: ReturnType<typeof useTranslation>["t"];
}

/**
 * Focus-page table card. Owns the visual chrome (rounded card with border
 * + shadow), the top bar (title + ModifyControls), the table itself, and
 * the totals footer. Splits responsibilities away from the legacy
 * PreOrderHeader so the focus page can present a clean, minimal layout
 * matching the spec mockup without changing the list-card UX.
 */
function FocusedTableCard({
  preOrder,
  flows,
  tableDisabled,
  buffer,
  columnResize,
  columns,
  density,
  t,
}: FocusedTableCardProps) {
  const lineCount = flows?.length ?? 0;

  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden">
      {/* min-h floors the bar at the chip height (h-7 + padding) so it stays a
          constant height whether or not the Valider/Annuler chips are mounted —
          otherwise the table jumps down a few px when modify mode opens. */}
      <div className="flex min-h-[3rem] items-center justify-between gap-3 px-4 py-1.5 border-b border-gray-300">
        <div className="flex items-baseline gap-2 min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">
            {t("ordersRecap.orderLines", {
              defaultValue: "Lignes de commande",
            })}
          </h2>
          <span className="text-xs text-gray-400 tabular-nums">
            · {lineCount}
          </span>
        </div>
        {/* The lifecycle/confirm actions live in the pinned focus hero bar
            (`FocusPrimaryActions` → `useRecapActionBar`). The table header
            keeps the local chips: Cancel + Save in modify mode, and the
            one-click "Accept AI" (accept + stay pending) whenever the row
            carries pending AI without buffered edits. */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <RecapModifyCancelSlot preOrder={preOrder} buffer={buffer} />
        </div>
      </div>
      <PreOrderCommentBlock preOrder={preOrder} />
      <FlowsTable
        preOrder={preOrder}
        flows={flows ?? []}
        disabled={tableDisabled}
        onFlowUpdate={buffer.handleFlowUpdate}
        onFlowCreate={buffer.handleFlowCreate}
        onFlowDelete={buffer.handleFlowDelete}
        partnerId={preOrder.partner_id}
        columnResize={columnResize}
        columns={columns}
        density={density}
        showTotalsFooter
      />
    </div>
  );
}
